import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type {
  AgentExecutionRoute,
  AgentExecutionStage,
  AgentTaskExternalSource,
  AgentTaskExternalSourceKind,
  AgentPermissionTier,
  AgentToolCallAudit,
  AgentToolCallAuditStatus,
  AgentTaskThreadEventType,
  AgentTaskPermissionState,
  AgentTaskRuntime,
  AgentTask,
  AgentTaskSource,
  AgentTaskStatus,
  AgentTaskThreadMessage,
  AgentThreadRole,
} from '../types';

function taskCollection(userId: string) {
  return collection(getFirebaseDb(), 'users', userId, 'agentTasks');
}

function taskDoc(userId: string, taskId: string) {
  return doc(getFirebaseDb(), 'users', userId, 'agentTasks', taskId);
}

function threadCollection(userId: string, taskId: string) {
  return collection(getFirebaseDb(), 'users', userId, 'agentTasks', taskId, 'thread');
}

function toolCallCollection(userId: string, taskId: string) {
  return collection(getFirebaseDb(), 'users', userId, 'agentTasks', taskId, 'toolCalls');
}

function normalizeTask(taskId: string, rawData: unknown): AgentTask {
  const data = (rawData && typeof rawData === 'object') ? rawData as Record<string, unknown> : {};
  const statusValue = data.status;
  const status: AgentTaskStatus =
    statusValue === 'active' || statusValue === 'completed' || statusValue === 'failed'
      ? statusValue
      : 'pending';

  const sourceValue = data.source;
  const source: AgentTaskSource =
    sourceValue === 'coaching_suggestion' || sourceValue === 'system' ? sourceValue : 'manual';

  const externalSourceRaw = data.externalSource;
  let externalSource: AgentTaskExternalSource | undefined;
  if (externalSourceRaw && typeof externalSourceRaw === 'object') {
    const sourceRecord = externalSourceRaw as Record<string, unknown>;
    const kind: AgentTaskExternalSourceKind = sourceRecord.kind === 'day_focus' ? 'day_focus' : 'task';
    const sourceId = typeof sourceRecord.sourceId === 'string' ? sourceRecord.sourceId : '';
    const sourceKey = typeof sourceRecord.sourceKey === 'string' ? sourceRecord.sourceKey : '';
    const sourceDate = typeof sourceRecord.sourceDate === 'string' ? sourceRecord.sourceDate : '';
    if (sourceId && sourceKey && sourceDate) {
      externalSource = { kind, sourceId, sourceKey, sourceDate };
    }
  }

  const runtimeRaw = data.runtime;
  let runtime: AgentTaskRuntime | undefined;
  if (runtimeRaw && typeof runtimeRaw === 'object') {
    const runtimeRecord = runtimeRaw as Record<string, unknown>;
    const asStringArray = (value: unknown): string[] => (
      Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        : []
    );
    const route: AgentExecutionRoute =
      runtimeRecord.route === 'browser' || runtimeRecord.route === 'voice' ? runtimeRecord.route : 'api_mcp';
    const stage: AgentExecutionStage =
      runtimeRecord.stage === 'queued'
      || runtimeRecord.stage === 'running'
      || runtimeRecord.stage === 'completed'
      || runtimeRecord.stage === 'failed'
        ? runtimeRecord.stage
        : 'queued';
    const permissionTier: AgentPermissionTier =
      runtimeRecord.permissionTier === 'tier2_call_confirm'
      || runtimeRecord.permissionTier === 'tier3_delegate'
        ? runtimeRecord.permissionTier
        : 'tier1_research';
    runtime = {
      jobId: typeof runtimeRecord.jobId === 'string' ? runtimeRecord.jobId : '',
      route,
      stage,
      permissionTier,
      scheduledFor: runtimeRecord.scheduledFor as AgentTaskRuntime['scheduledFor'],
      confirmationRequired: Boolean(runtimeRecord.confirmationRequired),
      confirmationGranted: Boolean(runtimeRecord.confirmationGranted),
      confirmationMessage: typeof runtimeRecord.confirmationMessage === 'string'
        ? runtimeRecord.confirmationMessage
        : undefined,
      safetyFindings: asStringArray(runtimeRecord.safetyFindings),
      reviewUrls: asStringArray(runtimeRecord.reviewUrls),
      screenshotUrls: asStringArray(runtimeRecord.screenshotUrls),
      startedAt: runtimeRecord.startedAt as AgentTaskRuntime['startedAt'],
      endedAt: runtimeRecord.endedAt as AgentTaskRuntime['endedAt'],
      estimatedCostUsd: Number.isFinite(Number(runtimeRecord.estimatedCostUsd))
        ? Number(runtimeRecord.estimatedCostUsd)
        : 0,
      lastError: typeof runtimeRecord.lastError === 'string' ? runtimeRecord.lastError : undefined,
    };
  }

  const permissionRaw = data.permissionState;
  let permissionState: AgentTaskPermissionState | undefined;
  if (permissionRaw && typeof permissionRaw === 'object') {
    const permissionRecord = permissionRaw as Record<string, unknown>;
    const state = permissionRecord.state === 'injected' || permissionRecord.state === 'revoked'
      ? permissionRecord.state
      : 'none';
    const capabilities = Array.isArray(permissionRecord.capabilities)
      ? permissionRecord.capabilities.filter((item): item is string => typeof item === 'string')
      : [];
    permissionState = {
      state,
      capabilities,
      injectedAt: permissionRecord.injectedAt as AgentTaskPermissionState['injectedAt'],
      revokedAt: permissionRecord.revokedAt as AgentTaskPermissionState['revokedAt'],
    };
  }

  return {
    id: taskId,
    userId: typeof data.userId === 'string' ? data.userId : '',
    title: typeof data.title === 'string' ? data.title : 'Untitled task',
    description: typeof data.description === 'string' ? data.description : '',
    status,
    source,
    externalSource,
    runtime,
    permissionState,
    createdAt: data.createdAt as AgentTask['createdAt'],
    updatedAt: data.updatedAt as AgentTask['updatedAt'],
    completedAt: data.completedAt as AgentTask['completedAt'],
  };
}

function normalizeThreadMessage(messageId: string, rawData: unknown): AgentTaskThreadMessage {
  const data = (rawData && typeof rawData === 'object') ? rawData as Record<string, unknown> : {};
  const roleValue = data.role;
  const role: AgentThreadRole = roleValue === 'agent' || roleValue === 'system' ? roleValue : 'user';
  const eventTypeValue = data.eventType;
  const eventType: AgentTaskThreadEventType =
    eventTypeValue === 'status'
    || eventTypeValue === 'tool_call'
    || eventTypeValue === 'tool_result'
    || eventTypeValue === 'error'
    || eventTypeValue === 'schedule'
      ? eventTypeValue
      : 'message';
  return {
    id: messageId,
    role,
    content: typeof data.content === 'string' ? data.content : '',
    createdAt: data.createdAt as AgentTaskThreadMessage['createdAt'],
    eventType,
  };
}

function normalizeToolCallAudit(docId: string, rawData: unknown): AgentToolCallAudit {
  const data = (rawData && typeof rawData === 'object') ? rawData as Record<string, unknown> : {};
  const statusRaw = data.status;
  const status: AgentToolCallAuditStatus =
    statusRaw === 'failed' || statusRaw === 'timeout' ? statusRaw : 'success';

  return {
    id: docId,
    toolName: typeof data.toolName === 'string' ? data.toolName : 'unknown_tool',
    jobId: typeof data.jobId === 'string' ? data.jobId : undefined,
    attempt: Number.isFinite(Number(data.attempt)) ? Number(data.attempt) : 1,
    startedAtMs: Number.isFinite(Number(data.startedAtMs)) ? Number(data.startedAtMs) : 0,
    endedAtMs: Number.isFinite(Number(data.endedAtMs)) ? Number(data.endedAtMs) : 0,
    durationMs: Number.isFinite(Number(data.durationMs)) ? Number(data.durationMs) : 0,
    status,
    inputSummary: typeof data.inputSummary === 'string' ? data.inputSummary : '',
    outputSummary: typeof data.outputSummary === 'string' ? data.outputSummary : undefined,
    errorMessage: typeof data.errorMessage === 'string' ? data.errorMessage : undefined,
  };
}

export function subscribeAgentTasks(
  userId: string,
  callback: (tasks: AgentTask[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(taskCollection(userId), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => normalizeTask(d.id, d.data())));
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}

export function subscribeAgentTaskThread(
  userId: string,
  taskId: string,
  callback: (messages: AgentTaskThreadMessage[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(threadCollection(userId, taskId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => normalizeThreadMessage(d.id, d.data())));
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}

export function subscribeAgentTaskToolCalls(
  userId: string,
  taskId: string,
  callback: (audits: AgentToolCallAudit[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(toolCallCollection(userId, taskId), orderBy('startedAtMs', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => normalizeToolCallAudit(d.id, d.data())));
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}

export async function createAgentTask(
  userId: string,
  input: {
    title: string;
    description: string;
    status?: AgentTaskStatus;
    source?: AgentTaskSource;
    externalSource?: AgentTaskExternalSource;
  }
): Promise<string> {
  const now = Timestamp.now();
  const status = input.status ?? 'pending';
  const source = input.source ?? 'manual';
  const ref = await addDoc(taskCollection(userId), {
    userId,
    title: input.title.trim(),
    description: input.description.trim(),
    status,
    source,
    externalSource: input.externalSource ?? null,
    runtime: {
      jobId: '',
      route: 'api_mcp',
      stage: 'queued',
      permissionTier: 'tier1_research',
      confirmationRequired: false,
      confirmationGranted: false,
      safetyFindings: [],
      reviewUrls: [],
      screenshotUrls: [],
      estimatedCostUsd: 0,
    },
    permissionState: {
      state: 'none',
      capabilities: [],
    },
    createdAt: now,
    updatedAt: now,
    completedAt: status === 'completed' ? now : null,
  });
  return ref.id;
}

export interface ControlCentreSyncSourceTask {
  sourceKey: string;
  sourceId: string;
  sourceDate: string;
  kind: AgentTaskExternalSourceKind;
  title: string;
  description: string;
}

export async function syncAgentTasksFromSources(
  userId: string,
  sourceTasks: ControlCentreSyncSourceTask[],
  existingAgentTasks: AgentTask[]
): Promise<void> {
  if (!sourceTasks.length) return;

  const existingBySourceKey = new Map<string, AgentTask>();
  for (const task of existingAgentTasks) {
    const key = task.externalSource?.sourceKey;
    if (key) existingBySourceKey.set(key, task);
  }

  for (const sourceTask of sourceTasks) {
    const existing = existingBySourceKey.get(sourceTask.sourceKey);
    if (!existing) {
      await createAgentTask(userId, {
        title: sourceTask.title,
        description: sourceTask.description,
        status: 'pending',
        source: 'coaching_suggestion',
        externalSource: {
          kind: sourceTask.kind,
          sourceId: sourceTask.sourceId,
          sourceKey: sourceTask.sourceKey,
          sourceDate: sourceTask.sourceDate,
        },
      });
      continue;
    }

    // Never overwrite a task the user has manually edited in Control Centre.
    if ((existing as AgentTask & { manuallyEdited?: boolean }).manuallyEdited) {
      continue;
    }

    const updates: Record<string, unknown> = {};
    const trimmedTitle = sourceTask.title.trim();
    // Only update the title from source — never overwrite user-edited descriptions.
    if (trimmedTitle && existing.title !== trimmedTitle) updates.title = trimmedTitle;
    if (existing.source !== 'coaching_suggestion') updates.source = 'coaching_suggestion';

    if (Object.keys(updates).length > 0) {
      updates.updatedAt = Timestamp.now();
      await updateDoc(taskDoc(userId, existing.id), updates);
    }
  }
}

export async function updateAgentTaskStatus(
  userId: string,
  taskId: string,
  status: AgentTaskStatus
): Promise<void> {
  const now = Timestamp.now();
  await updateDoc(taskDoc(userId, taskId), {
    status,
    updatedAt: now,
    completedAt: status === 'completed' ? now : null,
  });
}

export async function updateAgentTaskDetails(
  userId: string,
  taskId: string,
  input: { title: string; description: string }
): Promise<void> {
  const title = input.title.trim();
  if (!title) {
    throw new Error('Task title is required.');
  }
  await updateDoc(taskDoc(userId, taskId), {
    title,
    description: input.description.trim(),
    manuallyEdited: true,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteAgentTask(
  userId: string,
  taskId: string
): Promise<void> {
  const [threadSnap, toolCallSnap] = await Promise.all([
    getDocs(threadCollection(userId, taskId)),
    getDocs(toolCallCollection(userId, taskId)),
  ]);
  await Promise.all([
    ...threadSnap.docs.map((docSnap) => deleteDoc(docSnap.ref)),
    ...toolCallSnap.docs.map((docSnap) => deleteDoc(docSnap.ref)),
  ]);
  await deleteDoc(taskDoc(userId, taskId));
}

export async function addAgentTaskThreadMessage(
  userId: string,
  taskId: string,
  input: { role: AgentThreadRole; content: string; eventType?: AgentTaskThreadEventType }
): Promise<void> {
  const trimmed = input.content.trim();
  if (!trimmed) return;
  const now = Timestamp.now();
  await addDoc(threadCollection(userId, taskId), {
    role: input.role,
    content: trimmed,
    eventType: input.eventType ?? 'message',
    createdAt: now,
  });

  // Keep task updatedAt moving with thread activity.
  await updateDoc(taskDoc(userId, taskId), {
    updatedAt: now,
  });
}

export async function ensureAgentTaskHasSystemThreadSeed(userId: string, taskId: string): Promise<void> {
  const existing = await getDoc(taskDoc(userId, taskId));
  if (!existing.exists()) return;

  const messagesQuery = query(threadCollection(userId, taskId), where('role', '==', 'system'));
  const existingSystemMessages = await getDocs(messagesQuery);

  if (!existingSystemMessages.empty) return;

  await addDoc(threadCollection(userId, taskId), {
    role: 'system',
    content: 'Task created. Ready for API/MCP execution.',
    eventType: 'status',
    createdAt: Timestamp.now(),
  });
}
